import { useVideoProgress } from "@/hooks/useVideoProgress";
import { cn } from "@/lib/utils";
import Hls from "hls.js";
import { Maximize, Minimize, Pause, Play, Volume2, VolumeX } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

interface VideoPlayerProps {
	videoUrl?: string;
	moduleId: string;
	initialPosition: number;
	onComplete: () => void;
}

export function VideoPlayer({ videoUrl, moduleId, initialPosition, onComplete }: VideoPlayerProps) {
	const videoRef = useRef<HTMLVideoElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const [isMuted, setIsMuted] = useState(false);
	const [isFullscreen, setIsFullscreen] = useState(false);
	const [currentTime, setCurrentTime] = useState(0);
	const [duration, setDuration] = useState(0);
	const [showControls, setShowControls] = useState(true);
	const hideTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
	const completedRef = useRef(false);

	useVideoProgress(moduleId, videoRef);

	// Capture initialPosition at mount so HLS doesn't restart when parent re-renders
	const initialPositionRef = useRef(initialPosition);
	// Track the video URL ref so we can update HLS source without re-running the effect
	const videoUrlRef = useRef(videoUrl);
	// Stable video identity: path without signed query params
	const videoBase = videoUrl?.split("?")[0] ?? "";

	// Keep the URL ref current for any future use, but don't re-init HLS
	useEffect(() => {
		videoUrlRef.current = videoUrl;
	}, [videoUrl]);

	// HLS.js setup — only re-runs when the actual video resource changes
	// biome-ignore lint/correctness/useExhaustiveDependencies: videoBase is an intentional trigger — stable video identity (path without signed query params) that re-runs the effect when the resource changes, while videoUrlRef.current provides the full signed URL inside the effect body.
	useEffect(() => {
		const video = videoRef.current;
		const url = videoUrlRef.current;
		if (!video || !url) return;

		completedRef.current = false;
		let hls: Hls | null = null;

		if (Hls.isSupported()) {
			hls = new Hls({ startPosition: initialPositionRef.current });
			hls.loadSource(url);
			hls.attachMedia(video);
		} else if (video.canPlayType("application/vnd.apple.mpegurl")) {
			video.src = url;
			video.currentTime = initialPositionRef.current;
		}

		return () => {
			hls?.destroy();
		};
	}, [videoBase]);

	// Time updates
	useEffect(() => {
		const video = videoRef.current;
		if (!video) return;

		const onTimeUpdate = () => {
			setCurrentTime(video.currentTime);
			if (
				!completedRef.current &&
				video.duration > 0 &&
				video.currentTime / video.duration >= 0.9
			) {
				completedRef.current = true;
				onComplete();
			}
		};
		const onDurationChange = () => setDuration(video.duration);
		const onPlay = () => setIsPlaying(true);
		const onPause = () => setIsPlaying(false);

		video.addEventListener("timeupdate", onTimeUpdate);
		video.addEventListener("durationchange", onDurationChange);
		video.addEventListener("play", onPlay);
		video.addEventListener("pause", onPause);

		return () => {
			video.removeEventListener("timeupdate", onTimeUpdate);
			video.removeEventListener("durationchange", onDurationChange);
			video.removeEventListener("play", onPlay);
			video.removeEventListener("pause", onPause);
		};
	}, [onComplete]);

	const togglePlay = useCallback(() => {
		const video = videoRef.current;
		if (!video) return;
		if (video.paused) {
			video.play();
		} else {
			video.pause();
		}
	}, []);

	const toggleMute = useCallback(() => {
		const video = videoRef.current;
		if (!video) return;
		video.muted = !video.muted;
		setIsMuted(video.muted);
	}, []);

	const toggleFullscreen = useCallback(() => {
		const container = containerRef.current;
		if (!container) return;
		if (document.fullscreenElement) {
			document.exitFullscreen();
			setIsFullscreen(false);
		} else {
			container.requestFullscreen();
			setIsFullscreen(true);
		}
	}, []);

	const handleSeekBar = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		const video = videoRef.current;
		if (!video) return;
		video.currentTime = Number(e.target.value);
	}, []);

	const handleMouseMove = useCallback(() => {
		setShowControls(true);
		if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
		hideTimeoutRef.current = setTimeout(() => {
			if (videoRef.current && !videoRef.current.paused) {
				setShowControls(false);
			}
		}, 3000);
	}, []);

	const formatTime = (t: number) => {
		const m = Math.floor(t / 60);
		const s = Math.floor(t % 60);
		return `${m}:${s.toString().padStart(2, "0")}`;
	};

	return (
		<div
			ref={containerRef}
			className="relative aspect-video max-h-full w-full bg-black"
			onMouseMove={handleMouseMove}
			onMouseLeave={() => {
				if (isPlaying) setShowControls(false);
			}}
		>
			{/* biome-ignore lint/a11y/useMediaCaption: video content is educational, no captions available yet */}
			{/* biome-ignore lint/a11y/useKeyWithClickEvents: keyboard shortcuts handled at page level */}
			<video
				ref={videoRef}
				className="absolute inset-0 h-full w-full object-contain"
				onClick={togglePlay}
				playsInline
			/>

			{/* Controls overlay */}
			<div
				className={cn(
					"absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent px-4 pb-3 pt-8 transition-opacity duration-300 motion-reduce:transition-none",
					showControls ? "opacity-100" : "pointer-events-none opacity-0",
				)}
			>
				{/* Seek bar */}
				<input
					type="range"
					min={0}
					max={duration || 0}
					value={currentTime}
					onChange={handleSeekBar}
					className="mb-2 h-1 w-full cursor-pointer appearance-none rounded-full bg-white/20 accent-accent [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-accent"
					aria-label="Seek"
				/>

				<div className="flex items-center justify-between">
					<div className="flex items-center gap-3">
						<button
							type="button"
							onClick={togglePlay}
							className="text-white hover:text-accent"
							aria-label={isPlaying ? "Pause" : "Play"}
						>
							{isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
						</button>
						<button
							type="button"
							onClick={toggleMute}
							className="text-white hover:text-accent"
							aria-label={isMuted ? "Unmute" : "Mute"}
						>
							{isMuted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
						</button>
						<span className="text-xs text-white/70">
							{formatTime(currentTime)} / {formatTime(duration)}
						</span>
					</div>
					<button
						type="button"
						onClick={toggleFullscreen}
						className="text-white hover:text-accent"
						aria-label={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
					>
						{isFullscreen ? <Minimize className="h-5 w-5" /> : <Maximize className="h-5 w-5" />}
					</button>
				</div>
			</div>
		</div>
	);
}
